import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import { useHeadingRefocus } from '../shared/use_heading_refocus';
import BlockedPanel from './blocked_panel';
import PreviewPanel from './preview_panel';
import XlsxExport from './xlsx_export';
import { fetch_report_csv, fetch_report_data, fetch_report_status } from '../api';
import { fmt_count } from '../format';
import { report_file_name, status_view, table_view, xlsx_sheet } from './adapters';
import {
  HANDLER_BLOCKED,
  HANDLER_NO_DATA,
  HANDLER_PERMISSION,
  report_error
} from './error_handlers';

/*
 * PROD-7828 R10 — the Reports tab: the seven reports ("D96") with CSV (server), XLSX (built here from
 * the same JSON) and a preview, the "D97" blocked panel and the "D98" draft mark. The server decides
 * blocked and draft; this tab only shows them.
 */
const STATUS_LOAD_FAILED_TEXT = 'Could not load the reports for this month.';

const ReportsTab = ({ pay_month, month_label, on_error, on_open_audit }) => {
  // 'loading' | 'ready' | 'no_data' | 'error'
  const [state, setState] = useState('loading');
  const [status, setStatus] = useState(status_view(null));
  const [no_data_text, setNoDataText] = useState('');
  const [load_error_text, setLoadErrorText] = useState('');
  // `${key}:${kind}` of each download or preview in flight; each button turns only itself off.
  const [busy, setBusy] = useState({});
  // One error slot per report card (CD-70): a failed download says so on its own card.
  const [card_errors, setCardErrors] = useState({});
  const [preview, setPreview] = useState(null);
  const [xlsx, setXlsx] = useState(null);
  const [recalc_note, setRecalcNote] = useState('');

  // Responses for an earlier month, or an earlier status read, never land on this one.
  const month_ref = useRef(pay_month);
  month_ref.current = pay_month;
  const status_seq = useRef(0);
  const preview_seq = useRef(0);
  const xlsx_seq = useRef(0);
  const heading_ref = useRef(null);
  const on_focus = useHeadingRefocus(heading_ref);

  // A quiet re-read (after "Recalculate now") keeps the page, and the panel's outcomes, on screen.
  const load = useCallback(
    (quiet) => {
      status_seq.current += 1;
      const seq = status_seq.current;
      if (!quiet) setState('loading');
      fetch_report_status(pay_month)
        .then((data) => {
          if (seq !== status_seq.current) return;
          setStatus(status_view(data));
          setState('ready');
        })
        .catch((e) => {
          if (seq !== status_seq.current) return;
          const err = report_error(e);
          if (err.handler === HANDLER_PERMISSION) {
            on_error(e, STATUS_LOAD_FAILED_TEXT);
            return;
          }
          if (err.handler === HANDLER_NO_DATA) {
            setNoDataText(err.message);
            setState('no_data');
            return;
          }
          setLoadErrorText(err.message || STATUS_LOAD_FAILED_TEXT);
          setState('error');
        });
    },
    [pay_month, on_error]
  );

  useEffect(() => {
    setPreview(null);
    setCardErrors({});
    setBusy({});
    setRecalcNote('');
    load(false);
  }, [load]);

  const set_busy = (id, on) =>
    setBusy((b) => {
      const next = { ...b };
      if (on) next[id] = true;
      else delete next[id];
      return next;
    });

  const set_card_error = (key, text) =>
    setCardErrors((c) => {
      const next = { ...c };
      if (text) next[key] = text;
      else delete next[key];
      return next;
    });

  // Returns the text to show where the action was pressed, or '' when the error was routed elsewhere.
  const route_error = (e, fallback) => {
    const err = report_error(e);
    if (err.handler === HANDLER_PERMISSION) {
      on_error(e, fallback);
      return '';
    }
    if (err.handler === HANDLER_BLOCKED) {
      // The month turned blocked after the status read: show its blockers.
      setStatus((s) => ({ ...s, blocked: true, blockers: err.blockers }));
      return '';
    }
    if (err.handler === HANDLER_NO_DATA) {
      setNoDataText(err.message);
      setState('no_data');
      return '';
    }
    return err.message || fallback;
  };

  const run_action = (report, kind, request, on_ok, fallback) => {
    const id = `${report.key}:${kind}`;
    if (busy[id]) return;
    const asked_for = pay_month;
    set_busy(id, true);
    set_card_error(report.key, '');
    request()
      .then((res) => {
        if (asked_for !== month_ref.current) return;
        set_busy(id, false);
        on_ok(res);
      })
      .catch((e) => {
        if (asked_for !== month_ref.current) return;
        set_busy(id, false);
        set_card_error(report.key, route_error(e, fallback));
      });
  };

  const download_csv = (report) =>
    run_action(
      report,
      'csv',
      () => fetch_report_csv(report.key, pay_month),
      ({ csv, file_name }) => {
        if (!csv) {
          set_card_error(report.key, 'The CSV came back empty. Try again.');
          return;
        }
        saveAs(
          new Blob([csv], { type: 'text/csv;charset=utf-8' }),
          file_name || report_file_name(report.key, pay_month, status.draft.is_draft, 'csv')
        );
      },
      'Could not download the CSV. Try again.'
    );

  const download_xlsx = (report) =>
    run_action(
      report,
      'xlsx',
      () => fetch_report_data(report.key, pay_month, false),
      (data) => {
        xlsx_seq.current += 1;
        setXlsx({ token: xlsx_seq.current, key: report.key, sheet: xlsx_sheet(table_view(data)) });
      },
      'Could not build the XLSX file. Try again.'
    );

  const open_preview = (report) => {
    preview_seq.current += 1;
    const seq = preview_seq.current;
    const asked_for = pay_month;
    setPreview({ key: report.key, title: report.title, state: 'loading', table: null });
    fetch_report_data(report.key, pay_month, true)
      .then((data) => {
        if (seq !== preview_seq.current || asked_for !== month_ref.current) return;
        setPreview({
          key: report.key,
          title: report.title,
          state: 'ready',
          table: table_view(data)
        });
      })
      .catch((e) => {
        if (seq !== preview_seq.current || asked_for !== month_ref.current) return;
        const text = route_error(e, 'Could not load the preview.');
        setPreview(
          text
            ? {
                key: report.key,
                title: report.title,
                state: 'error',
                table: null,
                error_text: text
              }
            : null
        );
      });
  };

  const xlsx_done = (key) => (error) => {
    setXlsx(null);
    if (error) set_card_error(key, 'Could not build the XLSX file. Try again.');
  };

  const recalc_finished = (tally) => {
    const stopped = tally.stopped
      ? `Stopped after ${fmt_count(tally.done)} of ${fmt_count(tally.total)}. `
      : '';
    setRecalcNote(
      `${stopped}Recalculated ${fmt_count(tally.done - tally.failed - tally.skipped)} of ${fmt_count(
        tally.done
      )} ${tally.done === 1 ? 'teacher' : 'teachers'}.`
    );
    setPreview(null);
    load(true);
  };

  if (state === 'loading') return <CustomLoading />;
  if (state === 'no_data') {
    return <div className="tp-empty">{no_data_text}</div>;
  }
  if (state === 'error') return <LoadFailed text={load_error_text} on_retry={() => load(false)} />;

  const { blocked, draft } = status;

  return (
    <div className="tp-rp" onFocus={on_focus}>
      <div className="tp-card-hd">
        <span
          className="tp-card-hd-title"
          role="heading"
          aria-level={2}
          tabIndex={-1}
          ref={heading_ref}
        >
          Reports
        </span>
      </div>
      <div className="tp-frow">
        <span className="tp-rp-note">
          Reports read the latest stored payment data for {status.month_label || month_label} (
          {fmt_count(status.teacher_count)} {status.teacher_count === 1 ? 'teacher' : 'teachers'}).
          Preview a report here, or download the full report as CSV or XLSX.
        </span>
        <div className="tp-frow-right">
          <button type="button" className="tp-link tp-rp-audit-link" onClick={on_open_audit}>
            Audit trail for this month
          </button>
        </div>
      </div>

      {recalc_note ? (
        <div className="tp-banner tp-banner-info" role="status">
          {recalc_note}
        </div>
      ) : null}
      {draft.is_draft && !blocked ? (
        <div className="tp-banner tp-banner-warn">
          <b>{draft.label}</b>. Every file and the preview carry this mark until every teacher with
          payment data for the month is closed.
        </div>
      ) : null}
      {blocked ? (
        <BlockedPanel
          blockers={status.blockers}
          pay_month={pay_month}
          month_label={status.month_label || month_label}
          on_finished={recalc_finished}
          on_error={on_error}
        />
      ) : null}

      <div className="tp-rp-grid">
        {status.reports.map((r, i) => {
          const csv_busy = Boolean(busy[`${r.key}:csv`]);
          const xlsx_busy = Boolean(busy[`${r.key}:xlsx`]);
          return (
            <div key={r.key} className="tp-rp-card">
              <div className="tp-rp-card-title">
                {i + 1}. {r.title}
              </div>
              <div className="tp-rp-card-desc">{r.description}</div>
              {card_errors[r.key] ? (
                <div className="tp-rp-card-error" role="alert">
                  {card_errors[r.key]}
                </div>
              ) : null}
              {/* Blocked is state from elsewhere, so native disabled; a pressed download turns only
                  itself off, with aria-disabled so it keeps focus (CD-101, CD-114). */}
              <div className="tp-rp-actions">
                <button
                  type="button"
                  className="tp-btn-ghost"
                  disabled={blocked}
                  aria-label={`Preview ${r.title}`}
                  onClick={() => open_preview(r)}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="tp-btn-out"
                  disabled={blocked}
                  aria-disabled={csv_busy ? 'true' : undefined}
                  aria-label={`Download ${r.title} as CSV`}
                  onClick={() => download_csv(r)}
                >
                  {csv_busy ? 'Preparing…' : '↓ CSV'}
                </button>
                <button
                  type="button"
                  className="tp-btn-out"
                  disabled={blocked}
                  aria-disabled={xlsx_busy ? 'true' : undefined}
                  aria-label={`Download ${r.title} as XLSX`}
                  onClick={() => download_xlsx(r)}
                >
                  {xlsx_busy ? 'Preparing…' : '↓ XLSX'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {preview ? (
        <PreviewPanel
          preview={preview}
          on_close={() => setPreview(null)}
          on_retry={() => {
            const r = status.reports.find((x) => x.key === preview.key);
            if (r) open_preview(r);
          }}
        />
      ) : null}
      {xlsx ? (
        <XlsxExport key={xlsx.token} sheet={xlsx.sheet} on_done={xlsx_done(xlsx.key)} />
      ) : null}
    </div>
  );
};

export default ReportsTab;
